import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import leftBackgroundImage from '/img/project_authSideImage.jpg'; // background on left

/* -------- Redux -------- */
import { useDispatch, useSelector } from "react-redux";
import { requestPasswordReset, loginError, clearAuthError, clearUserErrors, clearUserSuccess } from "../../store/actions";
/* ------------------------- */

/* -------- packages -------- */
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner,
} from "reactstrap";
import { getErrorMessage } from "../../utils/utils";
/* ------------------------- */

export default function ForgotPassword() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const parameters = useSelector(state => state.Parameters);
  document.title = `${t('resetPassword')} | ${parameters.appName}`; //meta title
  
  const passwordReset = useSelector(state => state.Users);
  const [error, setError] = useState('');

  // États pour gérer les erreurs persistantes comme dans DynamicForm
  const [allErrors, setAllErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const resetForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t('errorValidEmail')).required(t('errorEnterEmail')),
    }),
    validateOnChange: false, // Désactive la validation automatique sur onChange comme dans DynamicForm
    validateOnBlur: true,
    onSubmit: (values) => {
      dispatch(requestPasswordReset(values.email));
    },
  });

  const inputEmailError = (resetForm.touched.email && resetForm.errors.email);

  useEffect(() => {
    if (error !== '') {
      dispatch(loginError(""));
      setError('');
    } else if (passwordReset.errorUser) {
      setError(passwordReset.errorUser);
      toast.error(getErrorMessage(passwordReset.errorUser));
      dispatch(clearUserErrors());
    } else if (passwordReset.success) {
      toast.success(t('passwordResetEmailSent'));
      setTimeout(() => {
        dispatch(clearUserSuccess());
        navigate('/login');
      }, 5000);
    }
  }, [passwordReset.loadingUser, passwordReset.errorUser, passwordReset.success]); // Utilise loadingUser

  // Gestion des erreurs de validation comme dans DynamicForm
  useEffect(() => {
    // Update touched fields
    setTouchedFields(prev => ({
      ...prev,
      ...resetForm.touched
    }));

    // Capture new errors and merge them with previous ones
    if (resetForm.errors && Object.keys(resetForm.errors).length > 0) {
      setAllErrors(prev => {
        const newErrors = { ...prev };
        
        // Add or update errors for touched fields
        Object.keys(resetForm.errors).forEach(fieldKey => {
          if (resetForm.touched[fieldKey]) {
            newErrors[fieldKey] = resetForm.errors[fieldKey];
          }
        });
        
        // Remove errors for fields that are now valid AND touched
        Object.keys(prev).forEach(fieldKey => {
          if (resetForm.touched[fieldKey] && !resetForm.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        
        return newErrors;
      });
    } else {
      // If no new errors, clean errors for touched fields that are now valid
      setAllErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(resetForm.touched).forEach(fieldKey => {
          if (resetForm.touched[fieldKey] && !resetForm.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        return newErrors;
      });
    }
  }, [resetForm.errors, resetForm.touched]);

  // Fonctions utilitaires pour la gestion des erreurs comme dans DynamicForm
  const getFieldError = (fieldKey) => {
    // Priority to current Formik errors, otherwise use preserved errors
    return resetForm.errors[fieldKey] || allErrors[fieldKey];
  };

  const hasFieldError = (fieldKey) => {
    return !!(resetForm.errors[fieldKey] || allErrors[fieldKey]);
  };

  const shouldShowError = (fieldKey) => {
    return (resetForm.touched[fieldKey] || touchedFields[fieldKey]) && hasFieldError(fieldKey);
  };

  // Gestion des erreurs API comme dans DynamicForm
  useEffect(() => {
    if (passwordReset.errorUser && passwordReset.errorUser.data && passwordReset.errorUser.data.errors) {
      passwordReset.errorUser.data.errors.forEach(err => {
        if (err.code && err.key) {
          resetForm.setFieldError(err.key, t(err.code));
        }
      });
    }
  }, [passwordReset.errorUser]);

  return (
    <Container fluid className="p-0 m-0 vh-100 overflow-hidden d-flex flex-column authenticationPage">
      <Row className="h-100 m-0">
        <Col lg={8} className="d-none d-lg-block p-0">
          <div className="auth-full-bg p-0 h-100">
            <div className="d-flex h-100 p-0">
              <img src={leftBackgroundImage} alt="" className="img-fluid" />
              <span className="logoLogin">
                <img src={parameters.logo} alt="logo" width={300}  className="logo"/>
            </span>
            </div>
          </div>
        </Col>

        <Col lg={4} className="p-0 authenticationContainer h-100 d-flex flex-column justify-content-between flex-grow-1">
          <Row className={"d-flex flex-column justify-content-center align-items-center h-25"}>
            <Col className={"cardHeader mg-0"}>
              <Col lg={12} className="col p-0">
                <div className="text-primary">
                  <h5 className="text-primary">{t('resetPassword')}</h5>
                  <p>{t('resetPasswordText')}</p>
                </div>
              </Col>
            </Col>
          </Row>
          
          <Row className={" d-flex flex-column align-items-center p-3 h-75"}>
            <Card className="overflow-hidden interactionContainer">
              <Form
                className="form-horizontal signIn"
                onSubmit={(e) => {
                  e.preventDefault();
                  resetForm.handleSubmit();
                  return false;
                }}
              >
                <div className="mb-3">
                  <Label className="form-label">{t('email')}</Label>
                  <Input
                    name="email"
                    className="form-control"
                    type="email"
                    placeholder={parameters.appName + '@example.com'}
                    onChange={resetForm.handleChange}
                    onBlur={resetForm.handleBlur}
                    value={resetForm.values.email || ""}
                    invalid={shouldShowError('email')} // Utilise la nouvelle fonction
                    data-cy="reset-email"
                  />
                  {shouldShowError('email') && (
                    <FormFeedback type="invalid" data-cy="reset-emailError">
                      {getFieldError('email')} {/* Utilise la nouvelle fonction */}
                    </FormFeedback>
                  )}
                </div>
                
                <div className="mt-3 d-grid">
                    <button 
                      className="btn btn-primary btn-block w-100 btnLText justify-content-center d-flex align-items-center" 
                      type="submit" 
                      data-cy="reset-submit"
                      disabled={passwordReset.loadingUser} // Utilise loadingUser au lieu de loading
                    >
                        {passwordReset.loadingUser ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                        </>
                        ) : (
                        t('sendResetLink')
                        )}
                    </button>
                </div>
              </Form>
            </Card>
          </Row>

          <Row className="h-25">
              <div className="cardFooter">
                <button className="btn btn-secondary btn-block" type="button" onClick={() => navigate('/login')}>
                  {t('backToLogin')}
                </button>
              </div>
          </Row>

        </Col>
      </Row>

      <ToastContainer />
    </Container>
  );
}